// =============================================================================
// LIVRET D'APPRENTISSAGE — Catégorie A (Moto)
// 4 compétences · 38 sous-compétences détaillées
// Chaque sous-compétence inclut : Objectif · Critères de réalisation · Leçon
// =============================================================================
// Référence : Livret d'apprentissage officiel de la catégorie A (REMC 2014)
// Mobilisable en formation initiale (auto-école) et en présentation jury CCS.
// =============================================================================

export const META_LIVRET = {
  titre: "Livret d'apprentissage — Catégorie A (Moto)",
  source: "Référentiel REMC 2014 — Catégorie A (motocyclette)",
  description: "Cadre officiel des 4 compétences à acquérir pour le permis A. Chaque sous-compétence comporte un objectif d'apprentissage, des critères de réalisation observables, et une leçon-type que tu peux suivre.",
};

// =============================================================================
// COMPÉTENCE 1 — MAÎTRISER le maniement de la moto (hors circulation)
// =============================================================================
const C1 = {
  code: 'C1',
  titre: "MAÎTRISER le maniement de la moto à vitesse réduite et à vitesse plus élevée, hors circulation",
  contexte: "Bloc HORS CIRCULATION (plateau). Compétences techniques de base et préparation au passage du plateau (épreuve hors circulation du permis A).",
  niveauGDE: 1,
  sousCompetences: [
    {
      code: 'C1-a',
      intitule: "Connaître et adopter les comportements sécuritaires en matière d'équipement du motard",
      objectif: "À l'issue de la séance, tu identifies l'EPI obligatoire et recommandé, vérifie l'ajustement du casque et des gants, et adopte le port systématique.",
      criteres: [
        "Tu cites les 2 EPI obligatoires (casque ECE 22-06 + gants CE EN13594) sans aide.",
        "Tu cites au moins 3 EPI recommandés (blouson, pantalon, dorsale EN1621, chaussures montantes).",
        "Tu ajustes correctement la jugulaire du casque (test de l'index sous mentonnière).",
        "Tu vérifies la date DOT du casque et identifie un casque ayant subi un choc.",
        "Tu montes sur la moto en équipement complet sans qu'on te le demande.",
      ],
      lecon: {
        contexte: "Séance d'1h en salle ou sur plateau, 1ère semaine de formation. Équipement réel à disposition (casque, gants, blouson, dorsale).",
        demarche: [
          "Accueil (5 min) : « Si tu chutes à 30 km/h en short et tongs, que se passe-t-il ? »",
          "Découverte (10 min) : démonstration du matériel + photos avant/après accident (avec/sans EPI).",
          "Apports (25 min) : pour chaque EPI → norme officielle (ECE 22-06, EN13594, EN1621), zones protégées, sanction R431-1 / R431-1-2.",
          "Pratique (15 min) : essai casque + gants + réglage jugulaire + inspection casque ancien vs neuf.",
          "Évaluation (5 min) : QCM oral 6 questions sur normes et sanctions.",
        ],
        phraseClef: "L'équipement, c'est ma carrosserie.",
      },
    },
    {
      code: 'C1-b',
      intitule: "Connaître la conception de la moto, ses principaux organes, les principales commandes et le tableau de bord",
      objectif: "À l'issue de la séance, tu identifies les commandes principales, les organes essentiels (moteur, transmission, freinage, suspensions) et interprète les voyants du tableau de bord.",
      criteres: [
        "Tu désignes au moins 6 commandes (guidon, embrayage, frein avant, frein arrière, sélecteur, accélérateur).",
        "Tu cites au moins 4 organes (moteur, transmission, freinage, suspensions, éclairage).",
        "Tu identifies 5 voyants du tableau de bord (huile, ABS, clignotants, plein phare, neutre).",
        "Tu distingues le contacteur principal du coupe-circuit.",
      ],
      lecon: {
        contexte: "Séance d'1h sur plateau, moto à l'arrêt. Moteur éteint puis allumé pour observation des voyants.",
        demarche: [
          "Accueil (5 min) : « Sais-tu nommer toutes les commandes de cette moto ? »",
          "Découverte (10 min) : tour de la moto, tu désignes et nomme.",
          "Apports (30 min) : commandes (description + position + fonction), organes (vulgarisation), tableau de bord (chaque voyant).",
          "Pratique (10 min) : tu mimes l'usage de chaque commande, lit le tableau de bord.",
          "Évaluation (5 min) : tour du véhicule désignation à l'aveugle.",
        ],
        phraseClef: "Avant de conduire, je connais ma machine.",
      },
    },
    {
      code: 'C1-c',
      intitule: "Maîtriser et déplacer la machine (sans l'aide du moteur)",
      objectif: "À l'issue de la séance, tu déplaces la moto à la poussette, en sécurité, dans toutes les directions (avant, arrière, virage à pied).",
      criteres: [
        "Tu maintiens l'équilibre de la moto à l'arrêt sur sa béquille latérale puis en la redressant.",
        "Tu pousses la moto droite sur 10 m sans déséquilibre.",
        "Tu fais demi-tour à la poussette dans un cercle de 4 m de diamètre.",
        "Tu recules la moto sur 3 m (par le guidon).",
      ],
      lecon: {
        contexte: "Séance 45 min sur plateau plat, moteur éteint. Moto choisie adaptée au ton gabarit (selle accessible).",
        demarche: [
          "Démonstration formateur (5 min) : poussette droite, virage, marche arrière.",
          "Décomposition (5 min) : position des mains (guidon ferme), corps (proche moto), pas latéraux.",
          "Pratique (25 min) : tu répètes chaque exercice. Feedback immédiat.",
          "Difficultés progressives (5 min) : pousser sur pente légère, en virage.",
          "Évaluation (5 min) : enchaînement complet sans aide.",
        ],
        phraseClef: "Maîtriser sa moto debout, c'est le premier pas pour la maîtriser en route.",
      },
    },
    {
      code: 'C1-d',
      intitule: "Adopter une position de conduite sécuritaire sur la machine — Descendre de la machine en sécurité",
      objectif: "À l'issue de la séance, tu adoptes une position de conduite ergonomique et descend de la moto de manière sécuritaire dans toutes les configurations.",
      criteres: [
        "Tu montes sur la moto par la gauche, en maintenant le frein avant.",
        "Position pieds : sur les repose-pieds, talons rentrés, genoux serrés au réservoir.",
        "Position bras : légèrement fléchis, mains relâchées sur le guidon.",
        "Position tête : regard horizontal, dos droit.",
        "Descente : pied gauche posé, pied droit basculé, frein avant maintenu.",
      ],
      lecon: {
        contexte: "Séance 30 min sur plateau, moto à l'arrêt sur béquille.",
        demarche: [
          "Démonstration (5 min) : montée + position + descente.",
          "Apports (10 min) : pourquoi cette position (sécurité, contrôle, fatigue).",
          "Pratique (10 min) : répétitions, corrections.",
          "Évaluation (5 min) : montée/descente fluide et sécurisée.",
        ],
        phraseClef: "Ma position détermine mon contrôle.",
      },
    },
    {
      code: 'C1-e',
      intitule: "Démarrer et s'arrêter en toutes circonstances",
      objectif: "À l'issue de la séance, tu démarres proprement (sans calage) et s'arrête à un point donné avec un freinage maîtrisé.",
      criteres: [
        "Démarrage sans calage sur 5 essais consécutifs.",
        "Démarrage en montée légère (10° de pente).",
        "Arrêt précis à ±1m d'un point donné.",
        "Arrêt d'urgence à 30 km/h sur 12 m maximum.",
      ],
      lecon: {
        contexte: "Séance 1h sur plateau, moteur tournant. Repères au sol pour les arrêts.",
        demarche: [
          "Démonstration formateur (5 min) : démarrage 1ère + accélération + arrêt à un point.",
          "Décomposition embrayage (10 min) : point de patinage, dosage.",
          "Pratique en ligne droite (30 min) : démarrages, arrêts progressifs.",
          "Difficulté +1 : démarrage en pente, arrêt à point précis.",
          "Évaluation (10 min) : enchaînement démarrage-circulation-arrêt précis.",
        ],
        phraseClef: "Démarrer sans caler, s'arrêter au mètre près — voilà le contrôle.",
      },
    },
    {
      code: 'C1-f',
      intitule: "Monter et rétrograder les rapports de vitesse",
      objectif: "À l'issue de la séance, tu changes de rapport (monter et rétrograder) au bon régime, sans à-coup ni calage.",
      criteres: [
        "Passage des 4 premiers rapports en accélération fluide.",
        "Rétrograder en décélération sans à-coup.",
        "Coordination embrayage-accélérateur sans bruit anormal.",
        "Régime moteur cohérent à chaque rapport (pas de sur-régime ni de sous-régime).",
      ],
      lecon: {
        contexte: "Séance 1h sur plateau, parcours en boucle 100m.",
        demarche: [
          "Démonstration (5 min) : montée 1→4 + rétrograde 4→1.",
          "Décomposition (10 min) : geste embrayage + sélecteur + dosage accélérateur.",
          "Pratique progressive (30 min) : 1ère + 2ème seulement, puis 1-2-3, puis 1-2-3-4.",
          "Rétrograder (10 min) : à l'approche d'un point d'arrêt.",
          "Évaluation (5 min) : enchaînement complet montée+rétrograde fluide.",
        ],
        phraseClef: "Le bon rapport au bon moment, c'est l'élégance de conduite.",
      },
    },
    {
      code: 'C1-g',
      intitule: "Maîtriser la réalisation d'un freinage",
      objectif: "À l'issue de la séance, tu réalises un freinage progressif (avant + arrière) avec une répartition 70/30 et s'arrête en sécurité.",
      criteres: [
        "Utilisation simultanée des 2 freins (avant + arrière).",
        "Répartition correcte ≈ 70 % avant, 30 % arrière.",
        "Pas de blocage de roue (ou intervention ABS adaptée).",
        "Distance d'arrêt cohérente avec la vitesse (cf. formule V²/150 sec).",
      ],
      lecon: {
        contexte: "Séance 1h sur plateau, parcours 50 m. Repères au sol.",
        demarche: [
          "Démonstration (5 min) : freinage avant seul → arrière seul → combiné.",
          "Apports théoriques (10 min) : transfert de masse, formule V²/150.",
          "Pratique (35 min) : freinage à 30 km/h sur 6 m, puis 50 km/h sur 18 m.",
          "Évaluation (10 min) : freinage à 50 km/h, arrêt < 20 m, sans blocage.",
        ],
        phraseClef: "Avant 70, arrière 30 — c'est ma sécurité.",
      },
    },
    {
      code: 'C1-h',
      intitule: "Connaître les incidences du regard sur la trajectoire et la stabilité",
      objectif: "À l'issue de la séance, tu diriges son regard loin devant et applique la règle « là où je regarde, la moto va » dans toutes les situations.",
      criteres: [
        "Regard porté à 10-15 m devant en circulation lente.",
        "Regard porté à 30-50 m en circulation à vitesse normale.",
        "Tête tournée dans le sens du virage (et non les yeux seuls).",
        "Absence de regard fixé sur le compteur ou sur les obstacles.",
      ],
      lecon: {
        contexte: "Séance 45 min sur plateau, parcours en slalom + virages serrés.",
        demarche: [
          "Démonstration (5 min) : formateur en slalom, élève observe la tête.",
          "Apports (10 min) : neurophysiologie du regard, principe « la moto suit l'œil ».",
          "Pratique (25 min) : slalom + virage avec consigne « regarde le 4ème plot ».",
          "Évaluation (5 min) : slalom enchaîné fluide avec regard loin.",
        ],
        phraseClef: "Là où je regarde, ma moto va.",
      },
    },
    {
      code: 'C1-i',
      intitule: "Tenir l'équilibre, maintenir la stabilité et guider la moto (sans et avec passager)",
      objectif: "À l'issue de la séance, tu maintiens l'équilibre à allure lente, exécute un parcours sinueux, et adapte sa conduite avec passager.",
      criteres: [
        "Parcours lent (rapide pas + roule) sur 10 m sans pose de pied.",
        "Slalom 5 plots espacés de 3 m exécuté sans toucher de plot.",
        "Demi-tour en U dans un cercle de 4 m.",
        "Avec passager : démarrage sans calage, équilibre maintenu, freinage adapté.",
      ],
      lecon: {
        contexte: "Séance 1h30 sur plateau. Passager simulé (poids 60-80 kg) à la 2ème moitié.",
        demarche: [
          "Démonstration (5 min) : parcours lent + slalom + demi-tour.",
          "Apports (10 min) : effet de gyroscopie, importance du regard.",
          "Pratique seul (45 min) : parcours progressifs.",
          "Pratique avec passager (25 min) : nouveau dosage embrayage, freinage anticipé.",
          "Évaluation (5 min) : enchaînement complet avec et sans passager.",
        ],
        phraseClef: "L'équilibre ne s'impose pas, il se cultive.",
      },
    },
    {
      code: 'C1-j',
      intitule: "Maîtriser l'utilisation de l'embrayage (sauf transmission automatique)",
      objectif: "À l'issue de la séance, tu utilises l'embrayage avec dosage fin (patinage contrôlé) dans les manœuvres à basse vitesse.",
      criteres: [
        "Identification précise du point de patinage.",
        "Dosage de l'embrayage pour avancer à allure lente sans accélérer.",
        "Pas de calage en démarrage en pente.",
        "Pas d'à-coup au démarrage (sortie d'embrayage progressive).",
      ],
      lecon: {
        contexte: "Séance 45 min sur plateau, focus exercices basse vitesse.",
        demarche: [
          "Démonstration (5 min) : point de patinage expliqué.",
          "Apports (5 min) : rôle de l'embrayage, friction, principe.",
          "Pratique (30 min) : exercices basse vitesse (rouler entre 2 et 5 km/h) au seul dosage embrayage.",
          "Évaluation (5 min) : démarrage en pente + ralenti maintenu.",
        ],
        phraseClef: "Le poignet gauche, c'est la finesse du motard.",
      },
    },
    {
      code: 'C1-k',
      intitule: "Maîtriser les techniques d'inclinaison",
      objectif: "À l'issue de la séance, tu maîtrises le contre-braquage (au-delà de 30 km/h) et incline la moto en virage selon une trajectoire de sécurité.",
      criteres: [
        "Contre-braquage clairement utilisé à partir de 30 km/h.",
        "Trajectoire extérieur → intérieur → extérieur respectée.",
        "Inclinaison du corps adaptée (regard tourné, épaules suivent).",
        "Vitesse adaptée à l'angle de virage (pas de freinage en virage).",
      ],
      lecon: {
        contexte: "Séance 1h sur plateau, parcours avec courbes calibrées (rayon 8 m, 12 m, 15 m).",
        demarche: [
          "Démonstration (5 min) : formateur effectue 2 virages, élève observe.",
          "Apports théoriques (10 min) : contre-braquage, gyroscopie, trajectoire sécurité.",
          "Pratique progressive (35 min) : virage large (rayon 15 m), puis rayon 12 m, puis 8 m.",
          "Évaluation (10 min) : enchaînement 3 virages fluide à 50 km/h.",
        ],
        phraseClef: "Mon virage commence à l'extérieur.",
      },
    },
    {
      code: 'C1-l',
      intitule: "Réaliser un freinage d'urgence",
      objectif: "À l'issue de la séance, tu réalises un freinage d'urgence à 50 km/h sur une distance ≤ 14 m (sec) sans chute.",
      criteres: [
        "Pression simultanée et progressive sur les 2 freins en moins de 0,2 s.",
        "Pas de blocage durable de roue (ABS toléré).",
        "Maintien du regard loin (pas vers le sol).",
        "Distance ≤ 14 m à 50 km/h sur sec.",
        "Pas de pose de pied avant l'arrêt complet.",
      ],
      lecon: {
        contexte: "Séance 1h sur plateau. Repères de freinage au sol. Exercice exigé à l'examen du plateau.",
        demarche: [
          "Démonstration (5 min) : freinage d'urgence à 50 km/h.",
          "Apports (5 min) : physiologie de l'urgence, transfert de masse, ABS.",
          "Pratique progressive (40 min) : 30 km/h → 40 → 50, par paliers.",
          "Évaluation (10 min) : 3 essais réussis consécutifs à 50 km/h, distance < 14 m.",
        ],
        phraseClef: "Le freinage d'urgence se prépare, il ne s'improvise pas.",
      },
    },
  ],
};

// =============================================================================
// COMPÉTENCE 2 — APPRÉHENDER la route (circulation normale)
// =============================================================================
const C2 = {
  code: 'C2',
  titre: "APPRÉHENDER la route et circuler dans des conditions normales",
  contexte: "Bloc EN CIRCULATION 1. Premières sorties en circulation. Conditions normales (météo OK, trafic modéré, jour).",
  niveauGDE: 2,
  sousCompetences: [
    {
      code: 'C2-a',
      intitule: "Connaître et respecter les règles de circulation",
      objectif: "À l'issue de la séance, tu cites et applique les règles essentielles du Code de la route en situation de circulation.",
      criteres: [
        "Respect des limitations de vitesse (50/80/110/130 km/h).",
        "Respect des règles de priorité (à droite, panneaux, feux).",
        "Respect des distances de sécurité (règle des 2 secondes).",
        "Aucune infraction observée sur une sortie de 20 min.",
      ],
      lecon: {
        contexte: "Séance 1h en circulation urbaine. Élève en suivi formateur (radio ou voiture suiveuse).",
        demarche: [
          "Pré-briefing (10 min) : rappel des règles clés.",
          "Conduite commentée formateur (15 min) : verbalise chaque application.",
          "Conduite commentée élève (25 min) : verbalise ce qu'il voit/applique.",
          "Débriefing (10 min) : analyse des infractions ou hésitations observées.",
        ],
        phraseClef: "Le Code, c'est ma carte d'identité de motard.",
      },
    },
    {
      code: 'C2-b',
      intitule: "Rechercher la signalisation, les indices utiles et en tenir compte",
      objectif: "À l'issue de la séance, tu balaies visuellement la signalisation, identifie les indices spécifiques 2RM (revêtement, animaux, ouvertures portières) et adapte sa conduite.",
      criteres: [
        "Balayage visuel ample (loin + proche + rétroviseurs toutes les 5-7 secondes).",
        "Lecture des panneaux à temps (pas de surprise).",
        "Identification de 3+ indices typiques 2RM par minute (graviers, plaques égout mouillées, voiture stationnée portière en risque…).",
        "Adaptation visible de la conduite aux indices identifiés.",
      ],
      lecon: {
        contexte: "Séance 1h en circulation mixte. Pédagogie active du regard.",
        demarche: [
          "Pré-briefing (10 min) : indices spécifiques 2RM (revêtement, mobilier urbain, comportements autres).",
          "Démonstration formateur (15 min) : conduite commentée, énumération des indices.",
          "Conduite élève (25 min) : verbalise les indices détectés.",
          "Débriefing (10 min) : indices manqués, conséquences potentielles.",
        ],
        phraseClef: "En moto, ce que je ne vois pas peut me blesser.",
      },
    },
    {
      code: 'C2-c',
      intitule: "Positionner le véhicule sur la chaussée et choisir la voie de circulation",
      objectif: "À l'issue de la séance, tu adoptes un positionnement sécuritaire (tiers gauche de sa voie) et choisit la voie adaptée à sa direction.",
      criteres: [
        "Position dans le 1/3 gauche de la voie en circulation normale.",
        "Ajustement de la position selon le contexte (camion à droite, voiture stationnée, etc.).",
        "Voie choisie cohérente avec la direction (gauche, droite, tout droit).",
        "Changement de voie : contrôle visuel + clignotant + réinsertion fluide.",
      ],
      lecon: {
        contexte: "Séance 1h en circulation, axes à plusieurs voies.",
        demarche: [
          "Pré-briefing (10 min) : pourquoi le 1/3 gauche (visibilité, marge, sécurité).",
          "Conduite commentée (15 min) : formateur démontre le positionnement.",
          "Pratique élève (25 min) : positionnement et changements de voie.",
          "Débriefing (10 min) : analyse des choix de positionnement.",
        ],
        phraseClef: "Ma place, c'est là où je suis vu et où je peux m'échapper.",
      },
    },
    {
      code: 'C2-d',
      intitule: "Adapter l'allure aux situations",
      objectif: "À l'issue de la séance, tu modules sa vitesse en fonction du contexte (visibilité, météo, trafic, signalisation), parfois en dessous des limites légales.",
      criteres: [
        "Anticipation de la décélération avant chaque situation à risque.",
        "Vitesse adaptée à la visibilité (ex : 30 km/h en sortie d'école).",
        "Reprise progressive sans à-coup.",
        "Régime moteur cohérent (pas de sur-régime).",
      ],
      lecon: {
        contexte: "Séance 1h en circulation variée (école, hôpital, marché, intersection complexe).",
        demarche: [
          "Pré-briefing (10 min) : article R413-17 (vitesse inadaptée).",
          "Conduite commentée formateur (15 min) : « ici je ralentis parce que… ».",
          "Pratique élève (25 min) : verbalise ses choix de vitesse.",
          "Débriefing (10 min) : situations où la vitesse a été inadaptée.",
        ],
        phraseClef: "La limite légale n'est pas une obligation d'aller à fond.",
      },
    },
    {
      code: 'C2-e',
      intitule: "Être en mesure de s'arrêter ou stationner et de repartir en sécurité",
      objectif: "À l'issue de la séance, tu t'arrêtes, stationne et repart en sécurité dans tous les contextes (en ville, sur trottoir si autorisé, en parking).",
      criteres: [
        "Choix d'un emplacement légal et sécurisant (pas de gêne piétons).",
        "Béquille latérale ou centrale utilisée correctement.",
        "Antivol systématiquement utilisé (anti-démarrage / chaîne).",
        "Reprise : contrôle 360°, démarrage progressif, vérification flux.",
      ],
      lecon: {
        contexte: "Séance 45 min, parcours urbain avec arrêts variés.",
        demarche: [
          "Pré-briefing (10 min) : règles de stationnement 2RM (R417-1 et suivants).",
          "Démonstration formateur (10 min) : stationnement béquille latérale + centrale.",
          "Pratique élève (20 min) : 5 arrêts/redémarrages.",
          "Évaluation (5 min) : arrêt en pente + redémarrage en sécurité.",
        ],
        phraseClef: "Stationner, c'est aussi rouler — il faut s'y préparer.",
      },
    },
    {
      code: 'C2-f',
      intitule: "Tourner à droite et à gauche",
      objectif: "À l'issue de la séance, tu réalises un tourner-à-droite et un tourner-à-gauche en sécurité, en respectant priorités et trajectoires.",
      criteres: [
        "Clignotant activé 30 m avant le carrefour minimum.",
        "Contrôle des rétroviseurs + angle mort.",
        "Trajectoire sans empiétement sur la voie opposée.",
        "Vitesse adaptée (5-15 km/h en virage serré).",
      ],
      lecon: {
        contexte: "Séance 45 min en circulation urbaine, carrefours variés.",
        demarche: [
          "Pré-briefing (10 min) : priorités gauche/droite, trajectoire en virage urbain.",
          "Démonstration (10 min) : tourner droite + gauche commenté.",
          "Pratique élève (20 min) : succession de virages.",
          "Débriefing (5 min).",
        ],
        phraseClef: "Avant de tourner, je signale, je contrôle, je vire.",
      },
    },
    {
      code: 'C2-g',
      intitule: "Détecter, identifier et franchir les intersections suivant le régime de priorité",
      objectif: "À l'issue de la séance, tu identifies le régime de priorité de chaque intersection et franchit en sécurité.",
      criteres: [
        "Identification correcte du régime (à droite / STOP / Cédez / feu).",
        "Application stricte du STOP (arrêt complet, pieds posés).",
        "Repérage des indices d'intersection (panneau, marquage, perspective).",
        "Décélération anticipée.",
      ],
      lecon: {
        contexte: "Séance 1h en circulation, recherche d'intersections variées.",
        demarche: [
          "Pré-briefing (10 min) : les 4 régimes + sanctions associées.",
          "Conduite commentée formateur (20 min) : verbalise chaque intersection.",
          "Pratique élève (25 min) : verbalise et applique.",
          "Débriefing (5 min) : intersections où tu as hésité.",
        ],
        phraseClef: "Une intersection, c'est 4 décisions en 4 secondes.",
      },
    },
    {
      code: 'C2-h',
      intitule: "Franchir les ronds-points et les carrefours à sens giratoire",
      objectif: "À l'issue de la séance, tu franchis ronds-points et giratoires en sécurité, en respectant priorités et règles de positionnement.",
      criteres: [
        "Cédez le passage respecté à l'entrée.",
        "Positionnement adapté (voie de droite si 1ère sortie, voie de gauche si > 2ème sortie).",
        "Clignotant droit avant la sortie souhaitée.",
        "Sortie fluide sans bloquer les autres.",
      ],
      lecon: {
        contexte: "Séance 45 min, parcours incluant 5-6 giratoires de complexité croissante.",
        demarche: [
          "Pré-briefing (10 min) : différence rond-point (R415-9) vs giratoire (R415-10).",
          "Démonstration (10 min) : entrer, choisir voie, sortir.",
          "Pratique élève (20 min) : 6 giratoires différents.",
          "Débriefing (5 min).",
        ],
        phraseClef: "Au giratoire, je cède, je me place, je sors.",
      },
    },
  ],
};

// =============================================================================
// COMPÉTENCE 3 — CIRCULER (conditions difficiles + autres usagers)
// =============================================================================
const C3 = {
  code: 'C3',
  titre: "CIRCULER dans des conditions difficiles et partager la route avec les autres usagers",
  contexte: "Bloc EN CIRCULATION 2. Niveau intermédiaire/avancé. Conditions complexes (météo, trafic, autres usagers).",
  niveauGDE: 2,
  sousCompetences: [
    {
      code: 'C3-a',
      intitule: "Évaluer et maintenir les distances de sécurité",
      objectif: "À l'issue de la séance, tu évalues et maintient une distance de sécurité ≥ 2 secondes en conditions normales et ≥ 4 secondes sous la pluie.",
      criteres: [
        "Distance ≥ 2s appliquée en conditions normales (test du panneau).",
        "Distance ≥ 4s appliquée sous pluie/visibilité réduite.",
        "Ajustement spontané en cas d'arrivée d'un véhicule par l'arrière.",
        "Aucune insertion brutale d'autres véhicules dans l'espace de sécurité.",
      ],
      lecon: {
        contexte: "Séance 1h en circulation, mix urbain/route.",
        demarche: [
          "Pré-briefing (10 min) : règle des 2s, formule V²/150, distance d'arrêt.",
          "Démonstration (10 min) : formateur commente sa distance.",
          "Pratique (35 min) : élève verbalise sa distance, ajuste.",
          "Évaluation (5 min) : 3 tests de la règle des 2s validés.",
        ],
        phraseClef: "Ma distance, c'est mon temps de réagir.",
      },
    },
    {
      code: 'C3-b',
      intitule: "Croiser, dépasser, être dépassé",
      objectif: "À l'issue de la séance, tu croises, dépasse et accepte d'être dépassé en sécurité, en respectant les règles et marges latérales.",
      criteres: [
        "Marge latérale ≥ 1 m en agglo, ≥ 1,5 m hors agglo lors d'un dépassement.",
        "Vérification rétroviseurs + angle mort avant tout dépassement.",
        "Clignotants utilisés au début et à la fin du dépassement.",
        "Lorsque tu es dépassé : il maintient sa trajectoire et son allure.",
      ],
      lecon: {
        contexte: "Séance 1h en circulation mixte (route + ville).",
        demarche: [
          "Pré-briefing (15 min) : règles de dépassement (R414-4 et suivants), marges latérales.",
          "Démonstration (10 min) : dépassement commenté.",
          "Pratique (30 min) : 3-4 dépassements + 2-3 dépassements subis.",
          "Débriefing (5 min).",
        ],
        phraseClef: "Doubler, c'est anticiper — pas improviser.",
      },
    },
    {
      code: 'C3-c',
      intitule: "Négocier les virages de la façon la plus sécurisante",
      objectif: "À l'issue de la séance, tu négocies les virages en circulation selon la trajectoire de sécurité, vitesse adaptée et vision optimale.",
      criteres: [
        "Trajectoire extérieur → intérieur → extérieur respectée.",
        "Vitesse adaptée à la visibilité et au rayon.",
        "Regard porté à la sortie du virage.",
        "Pas de freinage en virage (sauf urgence).",
      ],
      lecon: {
        contexte: "Séance 1h sur axes sinueux (route départementale ou montagne).",
        demarche: [
          "Pré-briefing (10 min) : trajectoire moto vs voiture, contre-braquage.",
          "Démonstration suiveur (15 min) : formateur te suit et commente par radio.",
          "Pratique (30 min) : enchaînement virages.",
          "Débriefing (5 min).",
        ],
        phraseClef: "Un virage bien préparé est un virage bien réussi.",
      },
    },
    {
      code: 'C3-d',
      intitule: "Communiquer avec les autres usagers",
      objectif: "À l'issue de la séance, tu utilises les outils de communication (clignotants, klaxon, regard, gestes) pour exprimer son intention.",
      criteres: [
        "Clignotants utilisés systématiquement.",
        "Klaxon utilisé brièvement uniquement en cas de danger.",
        "Contact visuel avec un piéton ou un autre usager pour vérifier intention.",
        "Gestes courtois (remerciement, demande de cession).",
      ],
      lecon: {
        contexte: "Séance 45 min en agglomération.",
        demarche: [
          "Pré-briefing (10 min) : moyens de communication moto, courtoisie routière.",
          "Démonstration (10 min) : usage commenté.",
          "Pratique (20 min) : focus sur communication.",
          "Débriefing (5 min).",
        ],
        phraseClef: "En moto, je communique pour exister.",
      },
    },
    {
      code: 'C3-e',
      intitule: "Connaître les caractéristiques des autres usagers et savoir se comporter à leur égard avec respect et courtoisie",
      objectif: "À l'issue de la séance, tu identifies les comportements typiques des piétons, cyclistes, voitures, poids lourds, et adapte sa conduite avec respect.",
      criteres: [
        "Piéton : ralentissement systématique aux passages protégés (R415-11).",
        "Cycliste : marge latérale, prudence en sas vélo (R412-31).",
        "Voiture : anticipation des changements de voie sans clignotant.",
        "Poids lourd : maintien d'une distance accrue, évitement des angles morts.",
      ],
      lecon: {
        contexte: "Séance 1h en circulation urbaine et mixte.",
        demarche: [
          "Pré-briefing (15 min) : tableau des caractéristiques par catégorie d'usagers.",
          "Conduite commentée (15 min) : identifier chaque usager et adapter.",
          "Pratique élève (25 min) : verbalise sa lecture.",
          "Débriefing (5 min) : situations marquantes.",
        ],
        phraseClef: "Sur la route, je partage — donc je respecte.",
      },
    },
    {
      code: 'C3-f',
      intitule: "Connaître les particularités des véhicules lourds et savoir se rendre visible",
      objectif: "À l'issue de la séance, tu évites les angles morts des poids lourds, se rend visible, et anticipe leurs trajectoires (notamment en virage).",
      criteres: [
        "Aucun positionnement en angle mort camion (côté droit, juste derrière).",
        "Évitement systématique du « cisaillement » en virage de PL.",
        "Distance accrue avec un camion (effets aérodynamiques).",
        "Visibilité optimisée (équipement clair, feu).",
      ],
      lecon: {
        contexte: "Séance 45 min sur axes fréquentés par PL (rocade, périphérique).",
        demarche: [
          "Pré-briefing (15 min) : photos angles morts + cisaillement virage.",
          "Conduite commentée (10 min).",
          "Pratique élève (15 min) : croisement, dépassement PL.",
          "Débriefing (5 min).",
        ],
        phraseClef: "Devant le camion, je suis visible. Derrière, je suis prudent.",
      },
    },
    {
      code: 'C3-g',
      intitule: "S'insérer, circuler et sortir d'une voie rapide",
      objectif: "À l'issue de la séance, tu t'insères sur autoroute/voie rapide à la bonne vitesse, circule en sécurité et sort proprement.",
      criteres: [
        "Insertion : accélération sur la voie d'accélération, vitesse à 100-130 km/h avant entrée.",
        "Circulation : voie de droite par défaut, dépassement par la gauche uniquement.",
        "Distance ≥ 2s respectée à haute vitesse.",
        "Sortie : clignotant 200 m avant, décélération sur la voie de décélération.",
      ],
      lecon: {
        contexte: "Séance 1h sur autoroute (uniquement avec élève en fin de formation).",
        demarche: [
          "Pré-briefing (15 min) : règles autoroute, vitesses, dépassement par la gauche.",
          "Démonstration (10 min) : entrée + circulation + sortie.",
          "Pratique (30 min) : 2-3 entrées et sorties.",
          "Débriefing (5 min).",
        ],
        phraseClef: "Sur autoroute, à 130 km/h, je vois loin et j'agis tôt.",
      },
    },
    {
      code: 'C3-h',
      intitule: "Conduire dans une file de véhicules et dans une circulation dense",
      objectif: "À l'issue de la séance, tu circules dans une file/embouteillage en sécurité, en maintenant attention et distances adaptées.",
      criteres: [
        "Distance adaptée même à faible vitesse (1 longueur de moto au minimum).",
        "Anticipation des arrêts en chaîne.",
        "Pas de zigzag inutile entre voitures.",
        "Repérage des fuites ou ouvertures de portières.",
      ],
      lecon: {
        contexte: "Séance 45 min en circulation dense (heure de pointe).",
        demarche: [
          "Pré-briefing (10 min) : risques spécifiques en file, ouverture portière.",
          "Conduite commentée (15 min).",
          "Pratique élève (15 min).",
          "Débriefing (5 min).",
        ],
        phraseClef: "Dans la file, je suis patient — ma vie passe avant 2 minutes.",
      },
    },
    {
      code: 'C3-i',
      intitule: "Connaître les règles relatives à la circulation inter-files. Savoir la pratiquer si les conditions de sécurité et la situation d'apprentissage le permettent",
      objectif: "À l'issue de la séance, tu connais les conditions légales de la circulation inter-files (CIF) et l'applique en sécurité si autorisée.",
      criteres: [
        "Connaissance du décret 2021-1107 (CIF autorisée sous conditions sur 21 départements expérimentaux).",
        "Vitesse ≤ 50 km/h en CIF.",
        "Écart vitesse avec voitures ≤ 30 km/h.",
        "Positionnement entre les 2 voies les plus à gauche uniquement.",
      ],
      lecon: {
        contexte: "Séance 1h, axe expérimentation CIF (autoroute embouteillée).",
        demarche: [
          "Pré-briefing (20 min) : conditions légales CIF, sanctions hors zone expérimentale.",
          "Démonstration (15 min) : CIF commentée.",
          "Pratique (20 min) : si conditions réunies uniquement.",
          "Débriefing (5 min).",
        ],
        phraseClef: "L'inter-files, c'est un privilège — pas un droit.",
      },
    },
    {
      code: 'C3-j',
      intitule: "Conduire quand l'adhérence et la visibilité sont réduites",
      objectif: "À l'issue de la séance, tu adaptes sa conduite à la pluie, au brouillard, à la nuit et aux revêtements glissants.",
      criteres: [
        "Vitesse réduite ≥ 20 % par rapport au sec.",
        "Distance de sécurité doublée (4s minimum).",
        "Freinage adouci sur sol mouillé.",
        "Éclairage adapté (croisement permanent, code en agglo si brouillard).",
      ],
      lecon: {
        contexte: "Séance 1h, si possible par mauvais temps. Sinon, en briefing complet.",
        demarche: [
          "Pré-briefing (20 min) : physique sol mouillé, peinture/plaque égout, nuit.",
          "Conduite commentée (15 min) : recherche des zones à risque.",
          "Pratique élève (20 min) : verbalisation des choix.",
          "Débriefing (5 min).",
        ],
        phraseClef: "Quand la météo change, mes réflexes changent.",
      },
    },
    {
      code: 'C3-k',
      intitule: "Conduire à l'abord et dans la traversée d'ouvrages routiers tels que les tunnels, les ponts…",
      objectif: "À l'issue de la séance, tu adaptes sa conduite aux particularités des tunnels et ponts (lumière, vent, signalisation).",
      criteres: [
        "Allumage du croisement avant entrée en tunnel.",
        "Adaptation à la luminosité (entrée/sortie).",
        "Anticipation du vent latéral sur les ponts.",
        "Distance accrue dans le tunnel.",
      ],
      lecon: {
        contexte: "Séance 45 min sur axe avec tunnel ou pont. Si pas accessible : théorique avec photos.",
        demarche: [
          "Pré-briefing (15 min) : risques tunnels (lumière, écho son, encombrement).",
          "Démonstration (10 min).",
          "Pratique (15 min).",
          "Débriefing (5 min).",
        ],
        phraseClef: "Le tunnel et le pont sont des transitions — j'y arrive préparé.",
      },
    },
  ],
};

// =============================================================================
// COMPÉTENCE 4 — PRATIQUER une conduite autonome, sûre et économique
// =============================================================================
const C4 = {
  code: 'C4',
  titre: "PRATIQUER une conduite autonome, sûre et économique",
  contexte: "Bloc final. Préparation à l'autonomie post-permis. Niveau GDE 3 (conscience des choix) et 4 (conscience de soi).",
  niveauGDE: 3,
  sousCompetences: [
    {
      code: 'C4-a',
      intitule: "Suivre un itinéraire de manière autonome",
      objectif: "À l'issue de la séance, tu suis un itinéraire défini (sans accompagnement direct) en mémorisant les indications ou en utilisant un GPS de manière sécurisée.",
      criteres: [
        "Préparation amont : itinéraire visualisé sur carte.",
        "Si GPS : positionné correctement (champ de vision, lecture sans détourner les yeux > 1s).",
        "Aucun arrêt non prévu pour vérifier.",
        "Trajet bouclé en autonomie.",
      ],
      lecon: {
        contexte: "Séance 1h, itinéraire 15-20 km défini en briefing. Formateur en suivi à distance.",
        demarche: [
          "Pré-briefing (15 min) : préparation d'itinéraire, choix du moyen (carte mémoire / GPS).",
          "Démonstration GPS (10 min) : usage en sécurité.",
          "Trajet autonome (30 min).",
          "Débriefing (5 min) : difficultés rencontrées.",
        ],
        phraseClef: "Bien préparer mon trajet, c'est rouler libre.",
      },
    },
    {
      code: 'C4-b',
      intitule: "Préparer et effectuer un trajet longue distance en autonomie",
      objectif: "À l'issue de la séance, tu prépares un trajet > 200 km (matériel, itinéraire, météo, pauses) et l'effectue en sécurité.",
      criteres: [
        "Vérification matériel : pneus, niveaux, chaîne, éclairage.",
        "Itinéraire planifié + alternatives.",
        "Météo consultée et anticipée (vêtements, équipements).",
        "Pauses prévues (1 pause toutes les 2h minimum).",
        "Trajet effectif sans incident.",
      ],
      lecon: {
        contexte: "Séance théorique 1h30 + trajet réel sur 1 demi-journée si possible.",
        demarche: [
          "Pré-briefing (30 min) : checklist longue distance, méthode SOFT (Sommeil, Optique, Fluides, Tension).",
          "Atelier préparation (30 min) : élève prépare son trajet.",
          "Conduite (3h) : si possible, sortie longue distance.",
          "Débriefing (15 min).",
        ],
        phraseClef: "Un long trajet bien préparé, c'est un long trajet bien réalisé.",
      },
    },
    {
      code: 'C4-c',
      intitule: "Connaître les principaux facteurs de risque, ceux plus spécifiques à la conduite des motocyclettes et les recommandations à appliquer",
      objectif: "À l'issue de la séance, tu cites les principaux facteurs de risque (vitesse, alcool, fatigue, équipement) et énonce les bonnes pratiques.",
      criteres: [
        "Citation de 4+ facteurs de risque (humains, véhicule, environnement, infrastructure).",
        "Connaissance des chiffres ONISR (689 tués 2RM en 2024).",
        "Citation de 3 recommandations par facteur.",
        "Capacité à reconnaître son propre profil de risque.",
      ],
      lecon: {
        contexte: "Séance théorique 1h30 en salle.",
        demarche: [
          "Pré-briefing (15 min) : présentation des 4 catégories de risques.",
          "Apports (45 min) : ONISR, MAIDS, recommandations.",
          "Atelier (25 min) : auto-évaluation du profil de risque.",
          "Évaluation (5 min) : QCM 10 questions.",
        ],
        phraseClef: "Connaître mes risques, c'est me protéger.",
      },
    },
    {
      code: 'C4-d',
      intitule: "Connaître les comportements à adopter en cas d'accident : Protéger, Alerter, Secourir",
      objectif: "À l'issue de la séance, tu cites et applique la procédure PAS (Protéger, Alerter, Secourir) en cas d'accident.",
      criteres: [
        "Protection : placement de la moto pour signaler + balises + gilet HV.",
        "Alerte : 15 / 17 / 18 / 112 — message clair (qui, où, quoi, quand, qui).",
        "Secours : gestes essentiels (ne pas déplacer le blessé, retirer le casque uniquement si nécessaire).",
      ],
      lecon: {
        contexte: "Séance 1h30 théorique + mise en situation.",
        demarche: [
          "Pré-briefing (15 min) : statistiques accidents moto, importance des 5 premières minutes.",
          "Apports (30 min) : procédure PAS détaillée, numéros d'urgence.",
          "Mise en situation (35 min) : simulation d'accident, role play.",
          "Débriefing (10 min).",
        ],
        phraseClef: "Devant un accident, je protège, j'alerte, je secoure — dans cet ordre.",
      },
    },
    {
      code: 'C4-e',
      intitule: "Connaître les dispositifs d'aide à la conduite du véhicule (ABS, aides à la navigation, régulateur, limiteur de vitesse)",
      objectif: "À l'issue de la séance, tu connais les principaux dispositifs d'aide, leur fonctionnement et leurs limites.",
      criteres: [
        "ABS : principe (anti-blocage), obligatoire 2RM > 125 cm³ depuis 2017.",
        "CBS : couple avant-arrière, sur scooters et certaines motos.",
        "Régulateur / Limiteur de vitesse : usage approprié sur autoroute.",
        "GPS / Navigation : intégrée vs smartphone, sécurité d'usage.",
      ],
      lecon: {
        contexte: "Séance 1h sur plateau ou en salle, démonstration sur la moto-école.",
        demarche: [
          "Pré-briefing (10 min) : panorama des aides modernes.",
          "Apports (30 min) : chaque dispositif détaillé + démos.",
          "Évaluation (10 min) : QCM 6 questions.",
          "Conclusion (10 min) : les aides ne remplacent pas le pilote.",
        ],
        phraseClef: "Les aides sont mes alliées — pas mes pilotes.",
      },
    },
    {
      code: 'C4-f',
      intitule: "Avoir des notions sur l'entretien et le dépannage de la motocyclette",
      objectif: "À l'issue de la séance, tu réalises les vérifications d'entretien courant et les petits dépannages d'urgence.",
      criteres: [
        "Vérification niveau d'huile et liquide refroidissement.",
        "Vérification pression et état des pneus.",
        "Tension et graissage de la chaîne.",
        "Petit dépannage : ampoule, batterie, fusible.",
        "Connaissance du contrôle technique 2RM (obligatoire depuis 15/04/2024).",
      ],
      lecon: {
        contexte: "Séance 1h30 sur plateau, moto en démonstration.",
        demarche: [
          "Pré-briefing (10 min) : importance de l'entretien, statistiques pannes 2RM.",
          "Démonstration (40 min) : checklist complète, point par point.",
          "Pratique élève (35 min) : effectue les vérifications.",
          "Débriefing (5 min).",
        ],
        phraseClef: "Une moto bien entretenue est une moto qui me protège.",
      },
    },
    {
      code: 'C4-g',
      intitule: "Pratiquer l'éco-conduite",
      objectif: "À l'issue de la séance, tu appliques les principes d'éco-conduite (anticipation, régime moteur, choix d'itinéraire) et réduit sa consommation.",
      criteres: [
        "Régime moteur entre 3000 et 5000 tr/min en circulation normale.",
        "Anticipation des arrêts (lever de gaz au lieu de freiner).",
        "Vitesse stabilisée (régulateur si possible).",
        "Choix d'itinéraire intelligent (éviter bouchons, dénivelé).",
      ],
      lecon: {
        contexte: "Séance 1h en circulation, mesure consommation avant/après.",
        demarche: [
          "Pré-briefing (15 min) : principes d'éco-conduite 2RM (rares mais existants).",
          "Démonstration (15 min) : conduite commentée éco vs sportive.",
          "Pratique élève (25 min) : application des principes.",
          "Débriefing (5 min) : comparaison consommation.",
        ],
        phraseClef: "Conduire éco, c'est conduire intelligent.",
      },
    },
  ],
};

export const COMPETENCES_LIVRET = [C1, C2, C3, C4];
